import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Lock, Mail, User as UserIcon, Eye, EyeOff, AlertCircle, ShoppingBag, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { bookData } from './bookData';
import { createCheckoutSession, redirectToCheckout } from '../../../utils/stripe';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutLineItemInput } from '../../../utils/stripe';
// Resolve the product image for a cart item (supplement image or book cover).
function getCartItemImage(item: CartItem): string | undefined {
  const name = item.name?.trim().toLowerCase();
  const normalizedName = name?.replace(/[^a-z]/g, '');
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name) ?? shopData.find(s => {
    const catalogName = s.name.toLowerCase().replace(/[^a-z]/g, '');
    return Boolean(normalizedName && (catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName)));
  });
  return p?.image;
}
// Build a Stripe-compatible line item reference from a cart item.
// Priority: 1) lookupKey on the item, 2) stripePriceId on the item,
//           3) shopData lookup by lookupKey (supplements), 4) null.
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) return {
    lookupKey: item.lookupKey
  };
  if (item.stripePriceId) return {
    priceId: item.stripePriceId
  };
  const supplement = shopData.find(p => p.id === item.productId);
  if (supplement?.lookupKey) return {
    lookupKey: supplement.lookupKey
  };
  return null;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Sign in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
type Mode = 'signin' | 'signup';
export function Checkout() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn,
    signUp
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [mode, setMode] = useState<Mode>('signup');
  // A return to checkout means payment was cancelled, so retain the cart and
  // discard the checkout-complete marker created before the Stripe redirect.
  useEffect(() => {
    window.sessionStorage.removeItem('sn_checkout_clear_pending');
  }, []);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty cart guard
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex flex-col items-center justify-center pt-32 pb-20 px-6" data-id="element-2001">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2002">
          <div className="w-20 h-20 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2003">
            <ShoppingBag className="w-8 h-8 text-[#9A9A90]" data-id="element-2004" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-2005">
            Your cart is empty.
          </h1>
          <p className="text-sm font-medium text-[#5A5A5A] mb-10" data-id="element-2006">
            Add a book or supplement to begin checkout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2007">
            <Link to="/books" className="px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2008">
              Browse Books <ArrowRight className="w-4 h-4" data-id="element-2009" />
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-white border border-[#E5E5E5] text-[#111111] hover:border-tealslate hover:text-tealslate text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2010">
              Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-2011" />
            </Link>
          </div>
        </div>
      </div>;
  }
  // Auth gate passed: show the payment step
  if (user && !authLoading) {
    return <PaymentStep data-id="element-2012" />;
  }
  // While auth is resolving, show a quiet loading state
  if (authLoading) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-2013">
        <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-2014" />
      </div>;
  }
  // Auth gate form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          setSubmitting(false);
          return;
        }
        await signUp(email, password, name.trim(), marketingOptIn);
      } else {
        await signIn(email, password);
      }
      // Stay on /checkout — user is now signed in, the if(user) branch above renders
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2015">
      <section className="pt-32 pb-8 px-6" data-id="element-2016">
        <div className="max-w-5xl mx-auto" data-id="element-2017">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2018">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-2" data-id="element-2019">
              {mode === 'signup' ? 'Create an account to continue.' : 'Sign in to continue.'}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-2020">
              An account lets you track orders, access digital downloads, and
              save info for next time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-6" data-id="element-2021">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12" data-id="element-2022">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-3" data-id="element-2023">
            {/* Mode toggle */}
            <div className="flex bg-white border border-[#E5E5E5] rounded-md p-1 mb-8 shadow-sm" data-id="element-2024">
              <button type="button" onClick={() => {
              setMode('signup');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signup' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2025">
                New Customer
              </button>
              <button type="button" onClick={() => {
              setMode('signin');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signin' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2026">
                Returning Customer
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2027">
              {mode === 'signup' && <div data-id="element-2028">
                  <label htmlFor="name" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2029">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-2030">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2031" />
                    <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-2032" />
                  </div>
                </div>}

              <div data-id="element-2033">
                <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2034">
                  Email
                </label>
                <div className="relative" data-id="element-2035">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2036" />
                  <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-2037" />
                </div>
              </div>

              <div data-id="element-2038">
                <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2039">
                  Password
                </label>
                <div className="relative" data-id="element-2040">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2041" />
                  <input id="password" type={showPassword ? 'text' : 'password'} required minLength={mode === 'signup' ? 6 : undefined} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} data-id="element-2042" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2043">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2044" /> : <Eye className="w-4 h-4" data-id="element-2045" />}
                  </button>
                </div>
                {mode === 'signin' && <div className="mt-3 text-right" data-id="element-2046">
                    <Link to="/forgot-password" className="text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-2047">
                      Forgot password?
                    </Link>
                  </div>}
              </div>

              {mode === 'signup' && <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-2048">
                  <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-2049" />
                  <span className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2050">
                    Send me updates on new books, supplements, and articles.
                  </span>
                </label>}

              {error && <div className="bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2051">
                  <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2052" />
                  <p className="text-sm text-burgundy" data-id="element-2053">{error}</p>
                </div>}

              <button type="submit" disabled={submitting} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 mt-4" data-id="element-2054">
                {submitting ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2055" />
                    {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                  </> : <>
                    {mode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
                    <ArrowRight className="w-4 h-4" data-id="element-2056" />
                  </>}
              </button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-2057">
            <CartSummary data-id="element-2058" />
          </motion.aside>
        </div>
      </section>
    </div>;
}
function PaymentStep() {
  const navigate = useNavigate();
  const {
    user,
    profile,
    updateProfile
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedAddresses = profile?.addresses ?? [];
  const defaultAddress = savedAddresses.find(a => a.isDefault) ?? savedAddresses[0];
  const [selectedAddressId, setSelectedAddressId] = useState<string>(defaultAddress ? defaultAddress.id : 'new');
  // Re-sync the selection once saved addresses load. When the user signs in
  // directly on /checkout, the profile (and its addresses) arrives AFTER this
  // component mounts, so the initial state falls back to 'new'. Whenever a
  // default address becomes available but the current selection is 'new' or no
  // longer matches a saved address, snap to the default so the saved address is
  // pre-selected instead of showing the blank manual form.
  useEffect(() => {
    if (!defaultAddress) return;
    const selectionIsValid = savedAddresses.some(a => a.id === selectedAddressId);
    if (!selectionIsValid) {
      setSelectedAddressId(defaultAddress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses.length, defaultAddress?.id]);
  // Shipping Address Form State
  const [recipientName, setRecipientName] = useState(user?.displayName ?? '');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [newDeliveryInstructions, setNewDeliveryInstructions] = useState('');
  // Whether the address picker is expanded to show all saved addresses.
  const [expanded, setExpanded] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const selectedAddr = savedAddresses.find(a => a.id === selectedAddressId);
  const unbuyable = items.filter(i => !getCheckoutRefFor(i));
  // Live checkout — creates a Stripe Checkout Session and redirects the
  // customer to Stripe to complete payment. On success Stripe returns the
  // customer to /account?order=success.
  const handleCheckout = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const lineItems = items.map(i => {
        const ref = getCheckoutRefFor(i);
        if (!ref) return null;
        return {
          ...ref,
          quantity: i.quantity
        };
      }).filter((x): x is NonNullable<typeof x> => x !== null);
      if (lineItems.length === 0) {
        setError('No items in your cart are available for purchase yet.');
        setSubmitting(false);
        return;
      }
      const origin = window.location.origin;
      const {
        url
      } = await createCheckoutSession({
        lineItems,
        successUrl: origin + '/account?order=success',
        cancelUrl: origin + '/checkout'
      });
      // Stripe unloads this app for payment. Mark this browser session so the
      // cart provider can clear persisted state before its return-page
      // hydration has a chance to restore the completed order's items.
      window.sessionStorage.setItem('sn_checkout_clear_pending', 'true');
      redirectToCheckout(url);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong starting checkout.');
      setSubmitting(false);
    }
  };
  // Save a brand-new address to the profile right here on the checkout
  // screen, then select it and collapse the picker.
  const handleSaveNewAddress = async () => {
    if (!recipientName.trim() || !addressLine1.trim() || !city.trim() || !stateProvince.trim() || !zip.trim() || !country.trim()) {
      setError('Please fill out all required shipping address fields.');
      return;
    }
    setSavingAddr(true);
    setError(null);
    try {
      const newAddress = {
        id: Math.random().toString(36).substring(2, 9),
        recipientName: recipientName.trim(),
        line1: addressLine1.trim(),
        line2: addressLine2.trim(),
        city: city.trim(),
        state: stateProvince.trim(),
        postalCode: zip.trim(),
        country: country.trim(),
        deliveryInstructions: newDeliveryInstructions.trim(),
        isDefault: savedAddresses.length === 0
      };
      await updateProfile({
        addresses: [...savedAddresses, newAddress]
      });
      setSelectedAddressId(newAddress.id);
      setExpanded(false);
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setStateProvince('');
      setZip('');
      setNewDeliveryInstructions('');
    } catch (err: any) {
      setError(err?.message ?? 'Could not save this address. Please try again.');
    } finally {
      setSavingAddr(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2059">
      <section className="pt-32 pb-24 px-6" data-id="element-2060">
        <div className="max-w-5xl mx-auto flex flex-col gap-6" data-id="element-2061">
          <div className="w-full" data-id="element-2062">
            <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2063">
              Shipping Address
            </h2>

            <div className="bg-white border border-[#E5E5E5] shadow-sm rounded-xl overflow-hidden" data-id="element-2064">
              {/* COLLAPSED — only the selected address with a subtle Change action */}
              {selectedAddressId !== 'new' && !expanded && selectedAddr && <div className="p-5" data-id="element-2065">
                  <div className="flex items-start gap-4" data-id="element-2066">
                    <div className="pt-1" data-id="element-2067">
                      <span className="block w-4 h-4 rounded-full border-2 border-tealslate flex items-center justify-center" data-id="element-2068">
                        <span className="block w-2 h-2 rounded-full bg-tealslate" data-id="element-2069" />
                      </span>
                    </div>
                    <div className="flex-1 flex items-start justify-between gap-4" data-id="element-2070">
                      <div data-id="element-2071">
                        <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2072">
                          {selectedAddr.recipientName}
                          {selectedAddr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2073">
                              Default
                            </span>}
                        </p>
                        <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2074">
                          {selectedAddr.line1}
                          {selectedAddr.line2 && <br data-id="element-2075" />}
                          {selectedAddr.line2}
                          <br data-id="element-2076" />
                          {selectedAddr.city}, {selectedAddr.state}{' '}
                          {selectedAddr.postalCode}
                          <br data-id="element-2077" />
                          {selectedAddr.country}
                        </p>
                      </div>
                      <button type="button" onClick={() => setExpanded(true)} className="shrink-0 text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-2078">
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Delivery instructions — read-only display of the note
                   saved to this address (editable from the Account page). */}
                  {selectedAddr.deliveryInstructions?.trim() && <div className="mt-4 pl-8" data-id="element-2079">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1.5" data-id="element-2080">
                        Delivery instructions
                      </p>
                      <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2081">
                        {selectedAddr.deliveryInstructions}
                      </p>
                    </div>}
                </div>}

              {/* EXPANDED — all saved addresses stacked, plus add-new affordance */}
              {selectedAddressId !== 'new' && expanded && <AnimatePresence initial={false} data-id="element-2082">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-2083">
                    {savedAddresses.length > 0 && <div className="divide-y divide-[#E5E5E5]" data-id="element-2084">
                        {savedAddresses.map(addr => <label key={addr.id} className={`block p-5 cursor-pointer transition-colors ${selectedAddressId === addr.id ? 'bg-[#F9F9F9]' : 'bg-white hover:bg-[#FAFAFA]'}`} data-id="element-2085">
                            <div className="flex items-start gap-4" data-id="element-2086">
                              <div className="pt-1" data-id="element-2087">
                                <input type="radio" name="shippingAddress" value={addr.id} checked={selectedAddressId === addr.id} onChange={() => {
                          setSelectedAddressId(addr.id);
                          setExpanded(false);
                        }} className="w-4 h-4 accent-tealslate" data-id="element-2088" />
                              </div>
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2089">
                                <div data-id="element-2090">
                                  <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2091">
                                    {addr.recipientName}
                                    {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2092">
                                        Default
                                      </span>}
                                  </p>
                                  <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2093">
                                    {addr.line1}
                                    {addr.line2 && <br data-id="element-2094" />}
                                    {addr.line2}
                                    <br data-id="element-2095" />
                                    {addr.city}, {addr.state} {addr.postalCode}
                                    <br data-id="element-2096" />
                                    {addr.country}
                                  </p>
                                </div>
                                {addr.deliveryInstructions?.trim() && <div className="sm:border-l sm:border-[#E5E5E5] sm:pl-4" data-id="element-2097">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1" data-id="element-2098">
                                      Delivery instructions
                                    </p>
                                    <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2099">
                                      {addr.deliveryInstructions}
                                    </p>
                                  </div>}
                              </div>
                            </div>
                          </label>)}
                      </div>}
                    <div className="p-5 border-t border-[#E5E5E5]" data-id="element-2100">
                      <button type="button" onClick={() => {
                    setRecipientName(user?.displayName ?? '');
                    setSelectedAddressId('new');
                  }} className="inline-flex items-center gap-1.5 text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-2101">
                        <Plus className="w-4 h-4" data-id="element-2102" />
                        Add new address
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>}

              {selectedAddressId === 'new' && <div className="p-6 md:p-8" data-id="element-2103">
                  <div className="space-y-5" data-id="element-2104">
                    <div data-id="element-2105">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2106">
                        Recipient Full Name *
                      </label>
                      <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2107" />
                    </div>
                    <div data-id="element-2108">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2109">
                        Address Line 1 *
                      </label>
                      <input type="text" required value={addressLine1} onChange={e => setAddressLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2110" />
                    </div>
                    <div data-id="element-2111">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2112">
                        Address Line 2 (Optional)
                      </label>
                      <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2113" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2114">
                      <div data-id="element-2115">
                        <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2116">
                          City *
                        </label>
                        <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2117" />
                      </div>
                      <div className="grid grid-cols-2 gap-5" data-id="element-2118">
                        <div data-id="element-2119">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2120">
                            State / Province *
                          </label>
                          <input type="text" required value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2121" />
                        </div>
                        <div data-id="element-2122">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2123">
                            ZIP / Postal *
                          </label>
                          <input type="text" required value={zip} onChange={e => setZip(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2124" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-2125">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2126">
                        Country *
                      </label>
                      <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2127" />
                    </div>
                    <div data-id="element-2128">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2129">
                        Delivery Instructions (Optional)
                      </label>
                      <textarea rows={3} value={newDeliveryInstructions} onChange={e => setNewDeliveryInstructions(e.target.value)} placeholder="e.g. Leave at the front door, gate code is 1234…" className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] placeholder:text-[#9A9A90] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2130" />
                    </div>

                    <div className="flex items-center gap-3 pt-2" data-id="element-2131">
                      <button type="button" onClick={handleSaveNewAddress} disabled={savingAddr} className="px-5 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-id="element-2132">
                        {savingAddr ? <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2133" />
                            Saving…
                          </> : 'Save address'}
                      </button>
                      {defaultAddress && <button type="button" onClick={() => {
                    setSelectedAddressId(defaultAddress.id);
                    setExpanded(false);
                  }} className="text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-2134">
                          Cancel
                        </button>}
                    </div>
                    <p className="text-xs text-[#9A9A90] leading-relaxed" data-id="element-2135">
                      This address will be saved to your account for future
                      orders.
                    </p>
                  </div>
                </div>}
            </div>

            {error && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2136">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2137" />
                <p className="text-sm text-burgundy" data-id="element-2138">{error}</p>
              </div>}
          </div>

          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-2139">
            <CartSummary data-id="element-2140" />

            {unbuyable.length > 0 && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2141">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2142" />
                <div className="text-sm text-burgundy" data-id="element-2143">
                  <p className="font-bold mb-1" data-id="element-2144">
                    Some items aren't available for purchase yet:
                  </p>
                  <ul className="list-disc ml-5" data-id="element-2145">
                    {unbuyable.map(i => <li key={i.productId} data-id="element-2146">{i.name}</li>)}
                  </ul>
                </div>
              </div>}
          </motion.aside>

          <div className="w-full" data-id="element-2147">
            <button type="button" onClick={handleCheckout} disabled={submitting || items.length === 0} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2" data-id="element-2148">
              {submitting ? <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2149" />
                  Redirecting to Stripe…
                </> : <>
                  <Lock className="w-4 h-4" data-id="element-2150" />
                  Pay {formatPrice(subtotal)} with Stripe
                </>}
            </button>

            <p className="text-sm text-[#5A5A5A] text-center mt-4" data-id="element-2151">
              Secure payment processed by Stripe. You'll return here once your
              order is complete.
            </p>
          </div>
        </div>
      </section>
    </div>;
}
function CartSummary() {
  const {
    items,
    subtotal,
    itemCount
  } = useCart();
  return <div className="bg-white border border-[#E5E5E5]" data-id="element-2152">
      <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between" data-id="element-2153">
        <h2 className="text-xl font-bold text-[#111111] tracking-tight" data-id="element-2154">
          Order Summary
        </h2>
        <span className="text-sm font-medium text-[#9A9A90]" data-id="element-2155">Price</span>
      </div>
      <ul data-id="element-2156">
        {items.map(item => {
        const image = getCartItemImage(item);
        return <li key={item.productId} className="px-6 py-5 border-b border-[#E5E5E5] last:border-b-0 flex gap-4" data-id="element-2157">
              {/* Image */}
              {image && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-2158">
                  <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-2159" />
                </div>}

              {/* Details */}
              <div className="flex-1 min-w-0" data-id="element-2160">
                <p className="text-sm sm:text-base font-bold text-[#111111] leading-snug" data-id="element-2161">
                  {item.name}
                </p>
                <p className="text-sm text-tealslate font-medium mt-1" data-id="element-2162">
                  In Stock
                </p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-2163">
                  {item.type === 'book' ? 'Digital Book' : 'Supplement'} · Qty{' '}
                  {item.quantity}
                </p>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right" data-id="element-2164">
                <p className="text-sm sm:text-base font-bold text-[#111111] whitespace-nowrap" data-id="element-2165">
                  {formatPrice(item.unitPrice * item.quantity)}
                </p>
              </div>
            </li>;
      })}
      </ul>
      <div className="px-6 py-5 border-t border-[#E5E5E5] text-right" data-id="element-2166">
        <p className="text-lg text-[#111111]" data-id="element-2167">
          Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
          <span className="font-bold" data-id="element-2168">{formatPrice(subtotal)}</span>
        </p>
        <p className="text-sm text-[#5A5A5A] mt-2" data-id="element-2169">
          Shipping and tax calculated at payment.
        </p>
      </div>
    </div>;
}